import { z } from "zod";
import {
  ColorOptionLabel,
  MiDetailFields,
  MiModal,
} from "@miplatform/components";
import {
  ASSIGNMENT_TEMPLATE_TYPES,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CONFIGURABLE_COLORS,
  ENTITY_NAMES,
  INPUT_TYPES,
  ORDER_TYPES_OPTIONS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { capitalize } from "@miplatform/utils";

const INITIAL_ORDER_DATA = {
  name: "",
  shortName: "",
  color: "",
  tradeId: "",
  requiresApproval: false,
  fulfillmentSequence: "",
  type: ASSIGNMENT_TEMPLATE_TYPES.INSTALL,
};

const INITIAL_APPROVAL_DATA = {
  confirmationLeadDays: "",
  confirmationLeadTime: "",
  approvalLeadDays: "",
  approvalLeadTime: "",
};

export function OrderTemplateModal({
  open,
  title,
  buttonTitle,
  onClose,
  trades,
  loading,
  onSubmit = () => null,
}) {
  const orderTemplateFields = [
    {
      label: `${ENTITY_NAMES.ORDER_TEMPLATE} Name`,
      name: "name",
      autoFocus: true,
      beforeOnChange: capitalize,
      width: "100%",
      required: true,
      validationRule: z
        .string()
        .min(1, `${ENTITY_NAMES.ORDER_TEMPLATE} Name is required`),
    },
    {
      label: "Trade",
      name: "tradeId",
      fieldType: INPUT_TYPES.SELECT,
      options: trades,
      width: "100%",
      required: true,
      validationRule: z.string().min(1, "Trade Name is required"),
    },
    {
      label: "Abbr.",
      name: "abbreviation",
      beforeOnChange: (value) => value.toUpperCase(),
      width: "100%",
      required: true,
      validationRule: z
        .string()
        .min(2, "Abbreviation must be 2-3 characters")
        .max(3, "Abbreviation must be 2-3 characters"),
    },
    {
      label: "Short Name",
      name: "shortName",
      width: "100%",
      required: true,
      validationRule: z.string().min(1, "Short Name is required"),
    },
    {
      label: "Code",
      name: "code",
      width: "100%",
      required: true,
      validationRule: z.string(),
    },
    {
      label: "Color",
      name: "color",
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      fullWidth: true,
      width: "100%",
      required: true,
      validationRule: z.string().min(1, "Color is required"),
    },
    {
      label: "Fulfillment Sequence",
      name: "fulfillmentSequence",
      width: "100%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((val) => !val || /^[0-9]+$/.test(val), {
          message: "Must be a number",
        })
        .or(z.literal("")),
    },
    {
      label: "Order Type",
      name: "homePhase",
      fieldType: INPUT_TYPES.SELECT,
      options: ORDER_TYPES_OPTIONS,
      required: true,
    },
    {
      label: "Requires Approval",
      name: "requiresApproval",
      width: "100%",
      fieldType: INPUT_TYPES.CHECKBOX,
      validationRule: z.boolean().optional(),
    },
  ];

  const orderApprovalFields = [
    {
      label: "Confirmation Lead Days",
      name: "confirmationLeadDays",
      width: "100%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((value) => value >= 0, {
          message: "Confirmation Lead Days must be greater than or equal to 0",
        })
        .or(z.literal("")),
    },
    {
      label: "Confirmation Lead Time",
      name: "confirmationLeadTime",
      width: "100%",
      fieldType: INPUT_TYPES.TIME,
      validationRule: z
        .string()
        .optional()
        .refine(
          (value) => {
            const [hours, minutes] = value.split(":").map(Number);
            return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
          },
          {
            message: "Confirmation Lead Time must be a valid time",
          }
        )
        .or(z.literal("")),
    },
    {
      label: "Approval Lead Days",
      name: "approvalLeadDays",
      width: "100%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((value) => value >= 0, {
          message: "Approval Lead Days must be greater than or equal to 0",
        })
        .or(z.literal("")),
    },
    {
      label: "Approval Lead Time",
      name: "approvalLeadTime",
      width: "100%",
      fieldType: INPUT_TYPES.TIME,
      validationRule: z
        .string()
        .optional()
        .refine(
          (value) => {
            const [hours, minutes] = value.split(":").map(Number);
            return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
          },
          {
            message: "Approval Lead Time must be a valid time",
          }
        )
        .or(z.literal("")),
    },
  ];

  const orderTemplateEdit = useEdit(INITIAL_ORDER_DATA, orderTemplateFields);
  const orderApprovalEdit = useEdit(INITIAL_APPROVAL_DATA, orderApprovalFields);

  const requiresApproval = orderTemplateEdit.getValue("requiresApproval");

  const handleModalClose = () => {
    onClose();
    orderTemplateEdit.reset();
    orderApprovalEdit.reset();
  };

  const handleSubmit = () => {
    const orderApprovalEditEdits = {
      ...orderApprovalEdit.edits,
      confirmationLeadTime: orderApprovalEdit.edits.confirmationLeadTime
        ? `${orderApprovalEdit.edits.confirmationLeadTime}:00`
        : "",
      approvalLeadTime: orderApprovalEdit.edits.approvalLeadTime
        ? `${orderApprovalEdit.edits.approvalLeadTime}:00`
        : "",
    };

    const data = { ...orderTemplateEdit.edits, type: INITIAL_ORDER_DATA.type };

    onSubmit(requiresApproval ? { ...data, ...orderApprovalEditEdits } : data);
  };

  return (
    <MiModal
      open={open}
      closeModal={handleModalClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleModalClose,
        },
        {
          name: buttonTitle,
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: handleSubmit,
          disabled:
            loading ||
            !orderTemplateEdit.isValid() ||
            (requiresApproval && !orderApprovalEdit.isValid()),
        },
      ]}
      title={title}
      topRightClose={orderTemplateEdit.reset}
      loading={loading}
    >
      <MiDetailFields
        detailFields={orderTemplateFields}
        edit={orderTemplateEdit}
      />
      {requiresApproval && (
        <MiDetailFields
          detailFields={orderApprovalFields}
          edit={orderApprovalEdit}
        />
      )}
    </MiModal>
  );
}
